'use client';

import InlineStyle from '@/components/InlineStyle';
import Link from 'next/link';
import { motion } from 'framer-motion';
import BookDemoSection from '@/components/BookDemoSection';
import { BookDemoButton, Button, FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import PlatformHeroPanel from '@/components/platform/PlatformHeroPanel';
import { slideInLeft, slideInRight, scaleIn, viewportOnce, spring, springSoft } from '@/lib/motion';
import { useTranslation } from '@/hooks/useTranslation';
import { Radio, Brain, Globe, Zap, Activity, BarChart3, Check, Sparkles } from 'lucide-react';

/* metadata in ./layout.js — this page is a client component for i18n + motion. */

/* Tints — all derived from the brand sky-blue (#0098FF) family. */
const TINTS = {
  blue:   { bg: '#EAF6FF', stroke: '#BFE3FF', accent: '#0098FF' },
  sky:    { bg: '#EAF3FF', stroke: '#C7DEFF', accent: '#2E90FF' },
  violet: { bg: '#E9EFFF', stroke: '#C6D6FF', accent: '#0057D4' },
  ice:    { bg: '#F2F9FF', stroke: '#D7EEFF', accent: '#5AB4FF' },
};

/* ── Deep-dive background SVGs ──────────────────────────────────────────── */
function ListeningWavesBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.65 }} aria-hidden="true">
      <defs>
        <radialGradient id="lw-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.18" />
          <stop offset="60%" stopColor={tint.accent} stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="200" cy="200" r="180" fill="url(#lw-glow)" />
      {[40, 80, 120, 160, 200].map((r, i) => (
        <circle key={r} cx="200" cy="200" r={r} fill="none" stroke={tint.accent} strokeOpacity={0.32 - i * 0.05} strokeWidth="1.2" strokeDasharray={i === 0 ? '0' : '3 5'} />
      ))}
      <circle cx="200" cy="200" r="6" fill={tint.accent} />
      <circle cx="200" cy="200" r="14" fill="none" stroke={tint.accent} strokeOpacity="0.35" strokeWidth="1" />
    </svg>
  );
}

function NetworkNodesBg({ tint }) {
  const nodes = [
    { x: 90, y: 80, r: 5 }, { x: 200, y: 60, r: 7 }, { x: 310, y: 90, r: 5 },
    { x: 130, y: 180, r: 9 }, { x: 270, y: 180, r: 6 }, { x: 80, y: 280, r: 5 },
    { x: 200, y: 310, r: 8 }, { x: 320, y: 270, r: 5 },
  ];
  const links = [[0, 1], [1, 2], [0, 3], [1, 3], [1, 4], [2, 4], [3, 4], [3, 5], [3, 6], [4, 6], [4, 7], [6, 7], [5, 6]];
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.7 }} aria-hidden="true">
      <defs>
        <radialGradient id="nn-glow" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0.12" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="400" height="400" fill="url(#nn-glow)" />
      {links.map(([a, b], i) => (
        <line key={i} x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y} stroke={tint.accent} strokeOpacity="0.25" strokeWidth="1" />
      ))}
      {nodes.map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r={n.r + 4} fill={tint.accent} fillOpacity="0.12" />
          <circle cx={n.x} cy={n.y} r={n.r} fill={tint.accent} fillOpacity="0.85" />
        </g>
      ))}
    </svg>
  );
}

function PulseWaveBg({ tint }) {
  return (
    <svg viewBox="0 0 400 400" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.7 }} aria-hidden="true">
      <defs>
        <linearGradient id="pw-fade" x1="0%" x2="100%">
          <stop offset="0%" stopColor={tint.accent} stopOpacity="0" />
          <stop offset="50%" stopColor={tint.accent} stopOpacity="0.5" />
          <stop offset="100%" stopColor={tint.accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      {[100, 150, 200, 250, 300].map((y) => (
        <line key={y} x1="0" y1={y} x2="400" y2={y} stroke={tint.accent} strokeOpacity="0.08" strokeWidth="1" />
      ))}
      <path d="M 0 200 L 60 200 L 80 200 L 95 160 L 110 240 L 125 130 L 140 270 L 155 200 L 200 200 L 240 200 L 255 175 L 270 225 L 285 200 L 400 200" fill="none" stroke="url(#pw-fade)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx="125" cy="130" r="3" fill={tint.accent} />
      <circle cx="140" cy="270" r="3" fill={tint.accent} fillOpacity="0.6" />
    </svg>
  );
}

/* ── Content ────────────────────────────────────────────────────────────── */
const HERO = {
  eyebrow: 'Platform Capability',
  title: 'Social Intelligence',
  tagline: 'AI-Powered Social Intelligence for Brands That Think Ahead',
  body: [
    'Locobuzz enables deep Social Intelligence through real-time listening and AI-powered insights — helping you strengthen brand presence and shape smarter social strategies.',
    'By analyzing millions of conversations across social, news, and forums, our platform identifies emerging trends, competitor shifts, and customer intent.',
  ],
};

/* Impact band — the dark "scale proof" moment under the hero. */
const IMPACT = [
  { value: '4.8M+', label: 'Conversations analyzed every month' },
  { value: '200+', label: 'Languages decoded with native context' },
  { value: '35%', label: 'Of queries auto-resolved with AI' },
  { value: '30+', label: 'Real-time data sources unified' },
];

const STACK_POINTS = [
  { Icon: Radio, title: 'Real-Time Listening', text: 'Track real-time conversations across 30+ digital sources, channels, and markets.', tint: 'blue' },
  { Icon: Brain, title: 'Audience Decoding', text: 'Decode complex emotions, behaviors, and personas for sharper targeting.', tint: 'violet' },
  { Icon: Globe, title: '200+ Languages', text: 'Localize strategies across 200+ languages with region-level insight.', tint: 'sky' },
  { Icon: Zap, title: 'SignalSense™', text: 'Detect early shifts in sentiment, volume, or virality before they spike.', tint: 'blue' },
  { Icon: Activity, title: 'ContextualPulse™', text: 'Map contextual responses beyond keywords — trust, frustration, intent.', tint: 'violet' },
  { Icon: BarChart3, title: 'Competitive Benchmarks', text: 'Benchmark competitors by voice share, sentiment, and digital presence.', tint: 'sky' },
];

const DEEP_DIVES = [
  {
    eyebrow: 'Real-Time Listening',
    title: 'Listen in Real Time Across Every Channel',
    lead: 'Track brand, product, and industry conversations as they happen on social, news, forums, and beyond.',
    bullets: [
      'Monitor 30+ digital sources including Instagram, X, Reddit, forums, and app stores',
      'Detect early spikes in volume or emotion with SignalSense™ to stay ahead of crises or virality',
      'Filter by product, topic, or region to uncover targeted, high-impact insights',
      'Convert unstructured noise into searchable, meaningful narratives without the overload',
    ],
    cta: { label: 'Start Listening', href: '#book-demo' },
    visual: { Icon: Radio, title: '30+ Sources', subtitle: 'Live Channels Tracked', Bg: ListeningWavesBg, tint: 'blue' },
  },
  {
    eyebrow: 'Audience Intelligence',
    title: 'Decode Audiences, Emotions, and Influence',
    lead: 'Go beyond sentiment to understand who’s talking, how they feel, and what they move.',
    bullets: [
      'Identify audience personas through emotion, tone, and behavioral cues',
      'Track complex sentiments like sarcasm, hype, or frustration using advanced AI',
      'Discover emerging influencers across mainstream and niche communities',
      'Map conversations to segments and align your messaging with real audience language',
    ],
    visual: { Icon: Brain, title: 'AI Personas', subtitle: 'Behavior & Tone Modeling', Bg: NetworkNodesBg, tint: 'ice' },
    reverse: true,
    dark: true,
  },
  {
    eyebrow: 'ContextualPulse™',
    title: 'Understand Emotional Resonance at Scale',
    lead: 'Decode how your content made audiences feel — curiosity, trust, frustration, or joy.',
    bullets: [
      'Use ContextualPulse™ to decode tone, subtext, and emotional cues at scale',
      'Track real-time engagement, sentiment shifts, and reaction velocity of your audience',
      'Identify signals of advocacy, curiosity, or backlash across campaigns',
      'Compare emotional impact across platforms, regions, and audience types',
    ],
    cta: { label: 'Measure Campaigns', href: '#book-demo' },
    visual: { Icon: Activity, title: 'ContextualPulse™', subtitle: 'Emotion at Scale', Bg: PulseWaveBg, tint: 'sky' },
  },
];

/* DRAFT FAQ copy — derived from this page's content; review/refine before rollout. */
const FAQS = [
  { q: 'What is social intelligence?', a: 'Social intelligence turns real-time conversations across social, news, and forums into actionable insight — understanding what audiences feel, what competitors are doing, and where trends are heading before they peak.' },
  { q: 'Which data sources and languages does Locobuzz cover?', a: 'Locobuzz listens across 30+ digital sources — including Instagram, X, Reddit, forums, news, and app stores — in 200+ languages, with region-level, geo-tagged insight.' },
  { q: 'How is this different from basic social listening?', a: 'Beyond tracking mentions, Locobuzz decodes emotion, intent, and influence with ContextualPulse™, and surfaces early shifts in sentiment or virality with SignalSense™ — so you act on signals, not just volume.' },
  { q: 'Can I benchmark against competitors?', a: 'Yes. Compare Share of Voice, sentiment, and channel dominance against competitors, with early alerts on their moves and on emerging whitespace opportunities.' },
  { q: 'How quickly can my team get started?', a: 'Most teams go live quickly with guided onboarding. Book a demo and a specialist will tailor a rollout to your brand, channels, and markets.' },
];

/* Hero product panel data — social-listening themed. */
const PANEL = {
  title: 'Brand Listening',
  badge: '30+ sources',
  kpiValue: 18432,
  kpiLabel: 'Mentions today',
  trend: [10, 16, 13, 21, 26, 22, 33, 30, 42, 52],
  trendDelta: '+42%',
  segments: [
    { label: 'Positive', pct: 58, tone: 'pos' },
    { label: 'Neutral', pct: 30, tone: 'neu' },
    { label: 'Negative', pct: 12, tone: 'neg' },
  ],
  feedLabel: 'Live mentions',
  feed: [
    { text: 'Genuinely impressed — support replied in minutes 🙌', meta: '@maya_writes', tag: 'X', tone: 'pos' },
    { text: 'Anyone else seeing the new feature roll out?', meta: 'r/gadgets', tag: 'Reddit', tone: 'neu' },
    { text: 'Packaging arrived damaged again — not great', meta: '@deepak.k', tag: 'Instagram', tone: 'neg' },
    { text: 'Full breakdown of the launch is live now', meta: '@the_review_co', tag: 'YouTube', tone: 'neu' },
    { text: 'Switched last month and the app is genuinely better', meta: '@anita_sees', tag: 'X', tone: 'pos' },
  ],
  chip: 'SignalSense™ · spike +42% mentions',
};

/* ── Reusable bits ──────────────────────────────────────────────────────── */
function IconTile({ Icon, tint, size = 'md' }) {
  const T = TINTS[tint];
  const dim = size === 'lg' ? 64 : 48;
  const iconSize = size === 'lg' ? 28 : 22;
  return (
    <div style={{ width: dim, height: dim, borderRadius: 14, background: T.bg, border: `1px solid ${T.stroke}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={iconSize} color={T.accent} strokeWidth={1.8} />
    </div>
  );
}

function VisualCard({ visual }) {
  const { t } = useTranslation();
  const T = TINTS[visual.tint];
  const { Bg, Icon } = visual;
  return (
    <div style={{ minHeight: 340, borderRadius: 'var(--radius-3xl)', padding: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', background: `linear-gradient(135deg, #ffffff 0%, ${T.bg} 100%)`, border: `1px solid ${T.stroke}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,152,255,0.06)', position: 'relative', overflow: 'hidden' }}>
      <Bg tint={T} />
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <IconTile Icon={Icon} tint={visual.tint} size="lg" />
        <div>
          <div style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 24, color: 'var(--text-heading)', lineHeight: 1.2, letterSpacing: '-0.015em' }}>{t(visual.title)}</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 6 }}>{t(visual.subtitle)}</div>
        </div>
      </div>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */
export default function SocialIntelligencePage() {
  const { t } = useTranslation();
  return (
    <>
      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section style={{ background: 'var(--gradient-hero)', position: 'relative', overflow: 'hidden', padding: 'var(--hero-pad)' }}>
        <div aria-hidden="true" className="si-hero-orbs">
          <span className="si-orb si-orb1" />
          <span className="si-orb si-orb2" />
        </div>
        <InlineStyle css={`
          .si-hero-orbs { position:absolute; inset:0; pointer-events:none; z-index:0; overflow:hidden; }
          .si-orb { position:absolute; border-radius:50%; filter:blur(70px); }
          .si-orb1 { width:680px; height:680px; top:-220px; left:6%; background:radial-gradient(circle at 40% 40%, rgba(0,152,255,0.22), rgba(0,152,255,0.05) 50%, transparent 72%); animation:siOrb1 22s ease-in-out infinite; }
          .si-orb2 { width:520px; height:520px; top:-120px; right:4%; background:radial-gradient(circle at 60% 30%, rgba(0,87,212,0.16), rgba(0,152,255,0.04) 55%, transparent 72%); animation:siOrb2 26s ease-in-out infinite; }
          @keyframes siOrb1 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(30px,-22px) scale(1.06);} }
          @keyframes siOrb2 { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(-26px,26px) scale(1.05);} }
          @media (prefers-reduced-motion: reduce){ .si-orb{animation:none;} }
        `} />

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <nav aria-label={t('Social Intelligence')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 20, padding: '8px 14px 8px 12px', borderRadius: 999, border: '1px solid var(--border-light)', background: 'var(--surface-page, #f5f7ff)', fontSize: 12.5, letterSpacing: '0.02em' }}>
            <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 0 3px rgba(34,197,94,0.16)' }} />
            <Link href="/platform/" style={{ color: 'var(--text-muted)', fontWeight: 600, textDecoration: 'none' }}>{t('Platform')}</Link>
            <span aria-hidden="true" style={{ color: 'var(--text-muted)', opacity: 0.45, fontWeight: 600 }}>›</span>
            <span style={{ color: 'var(--brand)', fontWeight: 700 }}>{t('Social Intelligence')}</span>
          </nav>

          <div className="lb-hero-grid">
            <div>
              <span className="lb-pill" style={{ marginBottom: 16 }}>{t(HERO.eyebrow)}</span>
              <motion.h1
                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.05 }}
                style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'var(--type-hero-title)', color: 'var(--text-heading)', lineHeight: 1.08, letterSpacing: '-0.02em', margin: '0 0 14px' }}
              >
                {t('Social')} <span className="serif-accent italic">{t('Intelligence')}</span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.12 }}
                style={{ fontSize: 'clamp(17px, 1.6vw, 20px)', color: 'var(--brand)', fontWeight: 600, margin: '0 0 18px', lineHeight: 1.4 }}
              >
                {t(HERO.tagline)}
              </motion.p>
              {HERO.body.map((p, i) => (
                <p key={i} style={{ fontSize: 15.5, color: 'var(--text-body)', lineHeight: 1.75, margin: i === 0 ? '0 0 12px' : '0 0 28px' }}>{t(p)}</p>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ ...springSoft, delay: 0.2 }}
                style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}
              >
                <BookDemoButton label={t('Book a Demo')} />
                <Button href="/platform/" variant="secondary" size="lg">{t('Explore Platform')}</Button>
              </motion.div>
            </div>

            <PlatformHeroPanel {...PANEL} />
          </div>
        </div>
      </section>

      {/* ── DARK IMPACT BAND ───────────────────────────────────────────── */}
      <section style={{ padding: 'var(--section-pad)' }}>
        <div className="container">
          <MetricsBand items={IMPACT} columns={4} />
        </div>
      </section>

      {/* ── THE STACK ──────────────────────────────────────────────────── */}
      <section className="lb-section-alt">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center', marginBottom: 48 }}>
            <span className="lb-pill" style={{ marginBottom: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Sparkles size={12} color="var(--brand)" strokeWidth={2.2} />
              {t('The Stack')}
            </span>
            <h2 className="lb-h2" style={{ marginBottom: 14 }}>{t('The Social Intelligence Stack Brands Use to')} <span className="serif-accent italic">{t('Stay Ahead')}</span></h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', maxWidth: 640, margin: '0 auto', lineHeight: 1.7 }}>{t('Because smart brands don’t just join conversations, they lead them.')}</p>
          </motion.div>

          <div className="lb-grid-3">
            {STACK_POINTS.map((p, i) => (
              <motion.div
                key={p.title}
                className="lb-card"
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ ...spring, delay: (i % 3) * 0.08 }}
                whileHover={{ y: -6 }}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <IconTile Icon={p.Icon} tint={p.tint} />
                <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 700, fontSize: 17, color: 'var(--text-heading)', lineHeight: 1.35, margin: '14px 0 8px' }}>{t(p.title)}</h3>
                <p style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.65, margin: 0 }}>{t(p.text)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DEEP DIVES intro ───────────────────────────────────────────── */}
      <section className="lb-section" style={{ paddingBottom: 8 }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={spring} style={{ textAlign: 'center' }}>
            <span className="lb-pill" style={{ marginBottom: 14 }}>{t('What you get')}</span>
            <h2 className="lb-h2" style={{ marginBottom: 14 }}>{t('The Full-Stack Behind')} <span className="serif-accent italic">{t('Sharper Social Intelligence')}</span></h2>
            <p style={{ fontSize: 16, color: 'var(--text-body)', maxWidth: 720, margin: '0 auto', lineHeight: 1.7 }}>{t('From listening to benchmarking, every layer is built to help your brand uncover insights, decode emotions, and lead conversations with instinct.')}</p>
          </motion.div>
        </div>
      </section>

      {DEEP_DIVES.map((d, i) => {
        const tone = d.dark
          ? { eyebrow: '#7FB3FF', title: '#ffffff', lead: 'rgba(255,255,255,0.82)', bullet: 'rgba(255,255,255,0.80)', checkBg: 'rgba(127,179,255,0.16)', checkBorder: 'rgba(127,179,255,0.4)', check: '#7FB3FF' }
          : { eyebrow: 'var(--brand)', title: 'var(--text-heading)', lead: 'var(--text-body)', bullet: 'var(--text-secondary)', checkBg: 'var(--brand-subtle)', checkBorder: '#BDD9FF', check: 'var(--brand)' };
        const bg = d.dark ? 'linear-gradient(160deg, #0D1B3E 0%, #0A1454 100%)' : (i % 2 === 0 ? '#ffffff' : '#F8FAFF');
        return (
          <section key={d.title} style={{ background: bg, padding: '72px 0', position: 'relative', overflow: 'hidden' }}>
            <div className="container">
              <div className="lb-hero-grid" style={{ paddingBottom: 0 }}>
                <motion.div variants={d.reverse ? slideInRight : slideInLeft} initial="hidden" whileInView="show" viewport={viewportOnce} style={{ order: d.reverse ? 2 : 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: tone.eyebrow, letterSpacing: '1.8px', textTransform: 'uppercase', marginBottom: 12 }}>{t(d.eyebrow)}</div>
                  <h3 style={{ fontFamily: 'var(--font-geist), system-ui, sans-serif', fontWeight: 800, fontSize: 'clamp(24px, 3vw, 34px)', color: tone.title, lineHeight: 1.18, letterSpacing: '-0.015em', margin: '0 0 14px' }}>{t(d.title)}</h3>
                  <p style={{ fontSize: 15.5, color: tone.lead, lineHeight: 1.75, margin: '0 0 24px' }}>{t(d.lead)}</p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {d.bullets.map((b, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <span aria-hidden="true" style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: tone.checkBg, border: `1.5px solid ${tone.checkBorder}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 3 }}>
                          <Check size={11} color={tone.check} strokeWidth={3} />
                        </span>
                        <span style={{ fontSize: 15, color: tone.bullet, lineHeight: 1.65 }}>{t(b)}</span>
                      </li>
                    ))}
                  </ul>
                  {d.cta && (
                    <div style={{ marginTop: 28 }}>
                      <Button href={d.cta.href} variant="cta" size="lg">{t(d.cta.label)}</Button>
                    </div>
                  )}
                </motion.div>

                <motion.div variants={scaleIn} initial="hidden" whileInView="show" viewport={viewportOnce} style={{ order: d.reverse ? 1 : 2 }}>
                  <VisualCard visual={d.visual} />
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}

      {/* ── SOCIAL PROOF ───────────────────────────────────────────────── */}

      {/* ── FAQ ────────────────────────────────────────────────────────── */}
      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={<>{t('faq.everything_you_need_to')}{' '}<span className="serif-accent italic">{t('faq.know')}</span></>}
          align="center"
        />
        <FaqAccordion items={FAQS.map((f) => ({ q: t(f.q), a: t(f.a) }))} columns={2} name="social-intelligence-faq" />
      </PageSection>

      <BookDemoSection />
    </>
  );
}
